import { LineSegments, Mesh, Scene } from 'three';

import {
	createLightVisualization,
	disposeLightVisualization,
	updateLightVisualization
} from './create-light-visualization';
import {
	getLightType,
	hasConeMesh,
	hasLineMesh,
	isHemisphereLightObjects,
	LIGHT_HELPER_DEFAULTS,
	LightHelperConfig,
	LightHelperObjects,
	LightType,
	RenderQuality,
	VisualizableLight
} from './light-helper.types';

/**
 * Manages visualization for a single light
 *
 * Creates and maintains visualization objects appropriate for the light type:
 * - SpotLight: cone + sphere + line
 * - DirectionalLight: sphere + line
 * - PointLight, AmbientLight, HemisphereLight, RectAreaLight: sphere only
 *
 * @example
 * ```typescript
 * const helper = new LightHelper(spotLight, { scene, visible: true, quality: 5 });
 *
 * // In render loop
 * helper.update();
 *
 * // Change quality
 * helper.quality = 10;
 *
 * // Clean up
 * helper.dispose();
 * ```
 */
export class LightHelper {
	/** The light being visualized */
	readonly light: VisualizableLight;

	/** The type of light */
	readonly lightType: LightType;

	/** Scene reference */
	#scene: Scene;

	/** Visualization objects */
	#objects: LightHelperObjects;

	/** Configuration */
	#coneRadius: number;
	#coneHeight: number;
	#visible: boolean;
	#quality: RenderQuality;

	constructor (light: VisualizableLight, config: LightHelperConfig) {
		this.light = light;
		this.lightType = getLightType(light);
		this.#scene = config.scene;
		this.#coneRadius = config.coneRadius ?? LIGHT_HELPER_DEFAULTS.CONE_RADIUS;
		this.#coneHeight = config.coneHeight ?? LIGHT_HELPER_DEFAULTS.CONE_HEIGHT;
		this.#visible = config.visible ?? false;
		this.#quality = config.quality ?? LIGHT_HELPER_DEFAULTS.QUALITY;

		// Create visualization objects appropriate for light type
		this.#objects = createLightVisualization(
			light,
			this.#scene,
			this.#coneRadius,
			this.#coneHeight,
			this.#visible,
			this.#quality
		);
	}

	/** Get visibility state */
	get visible (): boolean {
		return this.#visible;
	}

	/** Set visibility state */
	set visible (value: boolean) {
		this.#visible = value;
		const isOn = this.light.visible && this.light.intensity > 0;

		// Update visibility for all objects
		this.#objects.sphere.visible = value;

		if (hasConeMesh(this.#objects)) {
			this.#objects.cone.visible = value;
		}

		if (hasLineMesh(this.#objects)) {
			this.#objects.line.visible = value && isOn;
		}

		// Handle hemisphere light specific objects
		// Keep cones and rings visible even when light is off, just show them in "off" state
		if (isHemisphereLightObjects(this.#objects)) {
			this.#objects.skySphere.visible = value;
			this.#objects.groundSphere.visible = value;
			this.#objects.controlSphere.visible = value;
			this.#objects.axisLine.visible = value;
			this.#objects.skyArrow.visible = value;
			this.#objects.groundArrow.visible = value;
			for (const ring of this.#objects.gradientRings) {
				ring.visible = value;
			}
		}
	}

	/** Get the cone mesh (SpotLight only, undefined for other types) */
	get cone (): Mesh | undefined {
		return hasConeMesh(this.#objects) ? this.#objects.cone : undefined;
	}

	/** Get the sphere mesh */
	get sphere (): Mesh {
		return this.#objects.sphere;
	}

	/** Get the line (SpotLight and DirectionalLight only, undefined for other types) */
	get line (): LineSegments | undefined {
		return hasLineMesh(this.#objects) ? this.#objects.line : undefined;
	}

	/** Get the sky sphere (HemisphereLight only, undefined for other types) */
	get skySphere (): Mesh | undefined {
		return isHemisphereLightObjects(this.#objects) ? this.#objects.skySphere : undefined;
	}

	/** Get the ground sphere (HemisphereLight only, undefined for other types) */
	get groundSphere (): Mesh | undefined {
		return isHemisphereLightObjects(this.#objects) ? this.#objects.groundSphere : undefined;
	}

	/** Get the control sphere (HemisphereLight only, undefined for other types) */
	get controlSphere (): Mesh | undefined {
		return isHemisphereLightObjects(this.#objects) ? this.#objects.controlSphere : undefined;
	}

	/** Check if this is a hemisphere light helper */
	get isHemisphereLight (): boolean {
		return this.lightType === LightType.Hemisphere;
	}

	/** Get render quality */
	get quality (): RenderQuality {
		return this.#quality;
	}

	/** Set render quality (0=wireframe, 1-10=quality level) */
	set quality (value: RenderQuality) {
		if (this.#quality === value) return;
		this.#quality = value;
		this.recreateObjects();
	}

	/**
	 * Update visualization to match current light state
	 *
	 * Call this in your render loop to keep the helper synchronized
	 * with the light's position, rotation, and on/off state.
	 */
	update (): void {
		updateLightVisualization(
			this.light,
			this.#objects,
			this.#coneHeight,
			this.#visible
		);
	}

	/**
	 * Set cone dimensions (affects SpotLight cone size, sphere size for all types)
	 */
	setConeDimensions (radius: number, height: number): void {
		this.#coneRadius = radius;
		this.#coneHeight = height;
		this.recreateObjects();
	}

	/**
	 * Recreate visualization objects (used when quality or dimensions change)
	 */
	private recreateObjects (): void {
		disposeLightVisualization(this.light, this.#objects, this.#scene);
		this.#objects = createLightVisualization(
			this.light,
			this.#scene,
			this.#coneRadius,
			this.#coneHeight,
			this.#visible,
			this.#quality
		);
	}

	/**
	 * Dispose of all visualization objects
	 */
	dispose (): void {
		disposeLightVisualization(this.light, this.#objects, this.#scene);
	}
}

